// src/componentes/ModalPago.jsx
import React, { useEffect, useState, useRef } from "react";
import notify from "./common/notify";
import { dialogService } from "./common/dialogService";
import "./ModalPago.css";
import { processPayment } from "../services/deudaServices";
import { procesarDevengoComision } from "../services/procesarDevengoComision";
import { cerrarPromesasPorPago } from "../services/promesasCumplimientoAuto";
import { METODOS_PAGO, normalizeMetodoPago } from "../helpers/metodosPago";
import { toJsDate, toLocalISODate, hoyLocalISO } from "../helpers/fechas";

// HOTFIX v1.1.3: usar zona LOCAL para evitar off-by-one en input type="date"
const formatFechaInput = (value) => {
  const d = toJsDate(value);
  return d ? toLocalISODate(d) : "";
};

const ModalPago = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  pago,
  deudaId,
  editable = true,
  // Sprint ENG-3: el caller (DetalleDeuda) pasa si la deuda ya tiene un
  // pago marcado como enganche para deshabilitar el toggle (hard block:
  // 1 enganche por deuda).
  yaHayEnganche = false,
}) => {
  const [monto, setMonto] = useState("");
  const [metodo, setMetodo] = useState("efectivo");
  const [fecha, setFecha] = useState("");
  const [referencia, setReferencia] = useState("");
  const [notas, setNotas] = useState("");
  const [esEnganche, setEsEnganche] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const cardRef = useRef(null);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    setMonto(
      pago?.cantidad !== undefined && pago?.cantidad !== null
        ? String(pago.cantidad)
        : ""
    );
    setMetodo(normalizeMetodoPago(pago?.metodo || "efectivo").value);
    // HOTFIX v1.1.3: default a HOY LOCAL (no UTC) — antes daba off-by-one
    setFecha(formatFechaInput(pago?.fecha) || hoyLocalISO());
    setReferencia(pago?.referencia || "");
    setNotas(pago?.notas || "");
    setEsEnganche(String(pago?.tipo || "").toLowerCase() === "enganche");
  }, [pago, isOpen]);

  // A11Y (Sprint A11Y): cerrar con Escape y enfocar el primer campo al abrir.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !isSubmitting) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const t = setTimeout(() => firstFieldRef.current?.focus(), 50);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) return null;

  const metodosValidos = METODOS_PAGO.filter((item) => item.value !== "historico").map((item) => item.value);

  const handleGuardar = async () => {
    const cantidad = parseFloat(monto);

    if (isNaN(cantidad) || cantidad <= 0) {
      notify.error("Ingresa un monto válido");
      return;
    }

    if (!metodosValidos.includes(metodo)) {
      notify.error("Método de pago inválido");
      return;
    }

    if (!fecha) {
      notify.error("Selecciona una fecha válida");
      return;
    }

    if (normalizeMetodoPago(metodo).transferencia && !referencia.trim()) {
      notify.error("Captura la referencia de la transferencia o deposito");
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      if (!pago) {
        await processPayment(deudaId, cantidad, metodo, {
          fecha,
          referencia,
          notas,
          ...(esEnganche ? { tipo: "enganche" } : {}),
        });

        // v1.2 EST-A.1: post-hooks ahora son await + try/catch específicos.
        // Antes eran fire-and-forget con solo console.warn — si fallaban, los
        // hitos no se devengaban (sin CxP) y las promesas quedaban abiertas
        // infinitamente. Ahora notificamos al usuario para que sepa qué
        // quedó pendiente y pueda reintentarlo manualmente desde el detalle.
        try {
          const r = await procesarDevengoComision(deudaId);
          if (r?.devengados > 0) {
            notify.success(
              `${r.devengados} hito${r.devengados > 1 ? "s" : ""} de comisión devengado${r.devengados > 1 ? "s" : ""} y CxP generada${r.devengados > 1 ? "s" : ""}.`
            );
          }
          if (r?.errores?.length) {
            notify.error(
              `Pago OK, pero devengo reportó errores: ${r.errores.slice(0, 2).join(" | ")}`
            );
          }
        } catch (e) {
          console.error("procesarDevengoComision (ModalPago):", e);
          notify.error(
            `Pago registrado, pero el devengo de comisiones falló: ${e?.message || "error desconocido"}. Revisa manualmente desde el detalle de la deuda.`
          );
        }

        try {
          const n = await cerrarPromesasPorPago(deudaId, fecha, { monto: cantidad });
          if (n > 0) notify.success(`${n} promesa(s) marcadas como cumplidas`);
        } catch (e) {
          console.error("cerrarPromesasPorPago (ModalPago):", e);
          notify.error(
            `Pago registrado, pero el cierre automático de promesas falló: ${e?.message || "error desconocido"}. Revisa promesas pendientes desde Seguimiento de Cobranza.`
          );
        }
      } else {
        await onSave({
          ...pago,
          cantidad,
          metodo,
          fecha,
          referencia,
          notas,
          ...(esEnganche
            ? { tipo: "enganche" }
            : pago.tipo !== undefined
            ? { tipo: pago.tipo }
            : {}),
        });
      }

      notify.success(pago ? "Pago guardado correctamente" : "Pago registrado correctamente");
      onClose();
    } catch (err) {
      console.error("Error procesando pago:", err);
      notify.error(err?.message || "No se pudo procesar el pago");
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmarYEliminar = async () => {
    if (isSubmitting) return;

    if (await dialogService.confirm("¿Estás seguro de eliminar este pago?")) {
      try {
        setIsSubmitting(true);
        await onDelete();
        onClose();
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="modal-backdrop">
      <div
        className="modal-card"
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-pago-title"
      >
        <h2 className="modal-title" id="modal-pago-title">
          {editable ? (pago ? "Editar pago" : "Nuevo pago") : "Detalle del pago"}
        </h2>

        <label className="modal-label">
          Monto:
          <input
            ref={firstFieldRef}
            type="number"
            step="any"
            min="0"
            className="modal-input"
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
            onBlur={(e) => {
              const v = parseFloat(e.target.value);
              if (Number.isFinite(v) && v > 0) {
                // Normaliza a 2 decimales al perder foco (CV-4)
                setMonto((Math.round((v + Number.EPSILON) * 100) / 100).toFixed(2));
              }
            }}
            disabled={!editable || isSubmitting}
          />
        </label>

        <label className="modal-label">
          Fecha:
          <input
            type="date"
            className="modal-input"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            disabled={!editable || isSubmitting}
          />
        </label>

        <label className="modal-label">
          Método:
          <select
            className="modal-select"
            value={metodo}
            onChange={(e) => setMetodo(e.target.value)}
            disabled={!editable || isSubmitting}
          >
            {METODOS_PAGO.filter((item) => item.value !== "historico").map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </label>

        <label className="modal-label">
          Referencia:
          <input
            type="text"
            className="modal-input"
            value={referencia}
            onChange={(e) => setReferencia(e.target.value)}
            disabled={!editable || isSubmitting}
            placeholder={normalizeMetodoPago(metodo).transferencia ? "Banco, folio o comprobante" : "Folio, banco, #deposito, etc."}
            required={normalizeMetodoPago(metodo).transferencia}
          />
        </label>

        <label className="modal-label">
          Notas:
          <textarea
            className="modal-textarea"
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            disabled={!editable || isSubmitting}
            placeholder="Comentarios adicionales sobre el pago"
          />
        </label>

        {/* Sprint ENG-3: marcar pago como enganche.
            Si yaHayEnganche (otro pago ya está marcado) y este NO es el
            mismo pago en edición, deshabilitar el toggle. */}
        {(() => {
          const editandoEsteEnganche = String(pago?.tipo || "").toLowerCase() === "enganche";
          const disabled = !editable || isSubmitting || (yaHayEnganche && !editandoEsteEnganche);
          return (
            <label className="modal-label" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
              <input
                type="checkbox"
                checked={esEnganche}
                onChange={(e) => setEsEnganche(e.target.checked)}
                disabled={disabled}
              />
              <span>
                Marcar como pago de <strong>enganche</strong>
                {yaHayEnganche && !editandoEsteEnganche && (
                  <span style={{ color: "#dc2626", fontSize: 12, marginLeft: 8 }}>
                    (ya hay un enganche registrado en esta deuda)
                  </span>
                )}
              </span>
            </label>
          );
        })()}

        <div className="modal-buttons">
          {editable && pago && (
            <button
              className="btn-eliminar"
              onClick={confirmarYEliminar}
              disabled={isSubmitting}
            >
              Eliminar
            </button>
          )}

          <button className="btn-cancelar" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </button>

          {editable && (
            <button
              className="btn-guardar"
              onClick={handleGuardar}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? pago
                  ? "Guardando..."
                  : "Registrando..."
                : pago
                ? "Guardar cambios"
                : "Registrar pago"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalPago;
